import type {
  BoxCapacity,
  BoxCode,
  Variety,
} from "@/lib/catalog/types";
import { clientPriceOverride } from "@/lib/catalog/client-pricing";
import { tierPricePerStem, type PricingTier } from "@/lib/catalog/tier-pricing";

// Returns the per-stem price for a given length, or null if no entry matches.
export function pricePerStem(variety: Variety, length: number): number | null {
  const entry = variety.pricePerStem.find((p) => p.length === length);
  return entry?.usd ?? null;
}

// PASTE #247 CRIT-1: the AUTHORITATIVE per-stem price for a stored cart
// line, derived from the catalog and nothing else. This is the single
// source of truth used by the client (buildLine, via resolvePricePerStem)
// AND the server re-pricing + QB invoice-build, so a server re-derive
// equals exactly what a legit client stored - a tampered client price is
// caught (server re-pricing) and can never reach the invoice.
//
// PASTE #250: `clientId` makes it client-aware. When a per-client base
// override applies (standard-rose / garden-rose @ 50/60cm for a listed
// client), it wins; otherwise the global catalog price is returned
// exactly as before. The override is consulted FIRST so the same
// function drives both the client display (buildLine on a variety the
// server already priced for this client) and the server re-derive /
// invoice (which pass clientId explicitly) - keeping CRIT-1 intact.
//
// Mirrors buildLine's length resolution (length ?? variety.lengths[0])
// and the priceByColor override rules: a concrete colorKey uses its
// override; "mix" uses the cheapest override; otherwise the length-based
// price. Returns 0 only when the variety is genuinely unpriceable.
export function catalogPricePerStem(
  variety: Variety,
  lengthCm: number | null | undefined,
  colorKey?: string | null,
  clientId?: string | null,
  tier?: PricingTier | null,
): number {
  // PASTE #362: length-less varieties (specialty/filler, lengths:[]) are flat-
  // priced and carry no client-selected length. Fall through to the first
  // pricePerStem tier so they still price when lengthCm is null - this lets the
  // stored lengthCm be null (no phantom "50 cm" on any surface) without making
  // the variety unpriceable. Mirrors effectiveDefaultLength.
  const len = lengthCm ?? variety.lengths[0] ?? variety.pricePerStem[0]?.length;
  // Per-client base override wins over EVERYTHING (tier included).
  // Buckets never overlap a priceByColor variety, so a matching override
  // is unambiguous. Null falls through to the tier/global logic below.
  const override = clientPriceOverride(variety, len, clientId);
  if (override !== null) return override;
  // The resolved GLOBAL per-stem price (length or per-color), computed
  // exactly as before so a tier of undefined is byte-identical to legacy.
  const global = globalPricePerStem(variety, len, colorKey);
  // PASTE #394: the tier layer sits between the explicit override and the
  // global price. undefined/flower_shop tier -> null -> return `global`
  // unchanged (the CRITICAL INVARIANT for every existing client). The event
  // rule consumes `global`; the wholesaler tier reads its own table.
  const tierPrice = tierPricePerStem(variety, len, tier, global);
  if (tierPrice !== null) return tierPrice;
  return global;
}

// The GLOBAL per-stem price for a variety+length+color - the exact logic
// catalogPricePerStem used before PASTE #394's tier layer, extracted so both
// the return path AND the event tier (global x 1.10) read one source.
function globalPricePerStem(
  variety: Variety,
  len: number | undefined,
  colorKey?: string | null,
): number {
  const byLength = len !== undefined ? pricePerStem(variety, len) : null;
  const fallback = byLength ?? 0;
  if (!colorKey || !variety.priceByColor) return fallback;
  if (colorKey === "mix") {
    const all = Object.values(variety.priceByColor);
    return all.length > 0 ? Math.min(...all) : fallback;
  }
  return variety.priceByColor[colorKey] ?? fallback;
}

// Step 8.1: post-migration specialty + filler-green carry `lengths: []`
// since they no longer pick by length on the variety detail page. Callers
// that still need *some* length to pass into pricePerStem / boxCapacity
// fall through to the first pricePerStem entry, which by convention is
// {length: 60} for the flat-priced categories.
export function effectiveDefaultLength(variety: Variety): number {
  if (variety.lengths.includes(60)) return 60;
  if (variety.lengths.length > 0) return variety.lengths[0];
  return variety.pricePerStem[0]?.length ?? 60;
}

// Cheapest per-stem price across all available lengths.
export function minPricePerStem(variety: Variety): number {
  return Math.min(...variety.pricePerStem.map((p) => p.usd));
}

// Picks the boxSizes entry for a length. Falls back to "any" when the
// variety's boxSizes are length-agnostic (hydrangeas, specialty, fillers).
export function boxCapacityForLength(
  variety: Variety,
  length: number,
): BoxCapacity | null {
  const lengthKey = String(length);
  if (variety.boxSizes[lengthKey]) return variety.boxSizes[lengthKey];
  if (variety.boxSizes.any) return variety.boxSizes.any;
  return null;
}

// Returns the boxSizes entry for varieties without a length concept.
export function boxCapacityAny(variety: Variety): BoxCapacity | null {
  return variety.boxSizes.any ?? null;
}

const BOX_ORDER: BoxCode[] = ["EB", "QB", "HB"];

// Smallest box code whose capacity is non-null. Useful for the "starts at"
// price line on catalog cards and as the auto-placement default.
export function smallestAvailableBox(capacity: BoxCapacity): BoxCode | null {
  for (const code of BOX_ORDER) {
    if (capacity[code] !== null) return code;
  }
  return null;
}

// Bottom-up: stems = capacity[boxCode] (looked up directly from boxSizes).
export function stemsInBox(
  capacity: BoxCapacity,
  boxCode: BoxCode,
): number | null {
  return capacity[boxCode];
}

// Bunches are derived only when the variety is sold by bunch. Hydrangeas
// (sold by box, stemsPerBunch=null) cannot answer this.
export function bunchesInBox(
  variety: Variety,
  capacity: BoxCapacity,
  boxCode: BoxCode,
): number | null {
  if (variety.stemsPerBunch === null) return null;
  const stems = stemsInBox(capacity, boxCode);
  if (stems === null) return null;
  return stems / variety.stemsPerBunch;
}

// pricePerBunch = pricePerStem * stemsPerBunch. Null for box-unit varieties.
export function pricePerBunch(
  variety: Variety,
  length: number,
): number | null {
  if (variety.stemsPerBunch === null) return null;
  const stem = pricePerStem(variety, length);
  if (stem === null) return null;
  return stem * variety.stemsPerBunch;
}

// pricePerBox = pricePerStem * stemsInBox. Always derives from stem to avoid
// rounding artifacts that compound when going stem -> bunch -> box.
export function pricePerBox(
  variety: Variety,
  length: number,
  boxCode: BoxCode,
): number | null {
  const stem = pricePerStem(variety, length);
  const cap = boxCapacityForLength(variety, length);
  if (stem === null || cap === null) return null;
  const stems = stemsInBox(cap, boxCode);
  if (stems === null) return null;
  return stem * stems;
}

// Convenience for catalog cards: returns the three lines a card shows for
// non-hydrangea varieties (stem / bunch / smallest box). Length is the
// active length chip (default: smallest available, or sole length if one).
export type CardPriceLines = {
  stem: number;
  bunch: number | null;
  box: { code: BoxCode; stems: number; bunches: number | null; price: number } | null;
};

export function getCardPriceLines(
  variety: Variety,
  length: number,
): CardPriceLines | null {
  const stem = pricePerStem(variety, length);
  if (stem === null) return null;
  const bunch = pricePerBunch(variety, length);
  const cap = boxCapacityForLength(variety, length);
  let box: CardPriceLines["box"] = null;
  if (cap) {
    const code = smallestAvailableBox(cap);
    if (code) {
      const stems = stemsInBox(cap, code) ?? 0;
      const bunches = bunchesInBox(variety, cap, code);
      box = { code, stems, bunches, price: stem * stems };
    }
  }
  return { stem, bunch, box };
}

// Rounds USD to cents using banker-safe Math.round on cents.
export function roundUsd(n: number): number {
  return Math.round(n * 100) / 100;
}
